// packages/constants/src/homeFeed.js
//
// Home feed helpers shared by web and mobile (docs/home-feed.md). Pure
// functions only: card identity and dedupe across pages, analytics parameters
// (counts and categories, never titles or usernames), and honest date labels.

export const HOME_FEED_TABS = Object.freeze({ FOR_YOU: 'for_you', FOLLOWING: 'following' });

/** Append a page of cards, dropping any card already shown (by id). */
export function mergeFeedCards(existing, incoming) {
    const seen = new Set(existing.map((card) => card.id));
    const out = [...existing];
    for (const card of incoming || []) {
        if (!card || !card.id || seen.has(card.id)) continue;
        seen.add(card.id);
        out.push(card);
    }
    return out;
}

/**
 * What a card is, for analytics: card_type (ranking, editorial_title,
 * editorial_collection, editorial_list, recommendations, rating_group, coin)
 * and source (editorial, personalized, popular, community, following).
 */
export function feedCardAnalytics(card, { tab, position } = {}) {
    const params = {
        tab,
        card_type: card?.type ?? 'unknown',
        source: card?.source ?? 'unknown',
    };
    if (Number.isInteger(position)) params.position = position + 1;
    if (card?.type === 'recommendations' && card.reason?.code) params.reason_code = card.reason.code;
    return params;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "Oct 3" this year, "Oct 3, 2025" otherwise. A real calendar date, never
 * "new" — older content keeps its age when activity is scarce.
 */
export function formatFeedDate(value, now = new Date()) {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return '';
    const label = `${MONTHS[date.getMonth()]} ${date.getDate()}`;
    return date.getFullYear() === now.getFullYear() ? label : `${label}, ${date.getFullYear()}`;
}

/** "Published Oct 3" (and "· updated Oct 9" when the published version changed later that day or after). */
export function rankingDateLabel(ranking, now = new Date()) {
    const published = formatFeedDate(ranking?.firstPublishedAt, now);
    if (!published) return '';
    const updated = formatFeedDate(ranking?.updatedAt, now);
    const updatedLater = ranking?.updatedAt && new Date(ranking.updatedAt).getTime() - new Date(ranking.firstPublishedAt).getTime() > 24 * 3600 * 1000;
    return updatedLater && updated && updated !== published ? `Published ${published} · updated ${updated}` : `Published ${published}`;
}

/** "Rated 3 movies" / "Rated a movie" for a rating group card. */
export function ratingGroupHeadline(total) {
    const n = Number(total) || 0;
    return n === 1 ? 'Rated a movie' : `Rated ${n} movies`;
}

/** One-decimal star value as the rating scale shows it ("4.5", "4.0"). */
export function formatStars(rating) {
    const n = Number(rating);
    return Number.isFinite(n) ? n.toFixed(1) : '';
}

/** The title key the Watchlist uses (movie 603 and series 603 are different titles). */
export const feedTitleKey = (title) => `${title?.kind}:${Number(title?.tmdbId)}`;
